import { ComputedRef, ref, watch } from '@nuxtjs/composition-api';
import useBrandConfig from '@shared/composables/useBrandConfig';
import {
  getOptionsWithUnavailabilityStatus,
  handleUnavailableOptionSelection,
  setDefaultSelectedOptions,
  toVariantSummary,
} from '@shared/helpers/bundleVariants/multi-variant-utils';
import { ISelectedOption, IVariant, IVariantOption } from '@shared/types/IBundledProduct';

const useProductVariants = (
  options: ComputedRef<IVariantOption[]>,
  variants: ComputedRef<IVariant[]>,
) => {
  const { globalConfig } = useBrandConfig();
  const optionsState = ref<IVariantOption[]>([]);
  const selectedOptions = ref<ISelectedOption[]>([]);

  const updateSelectedOptions = (selectedOption: ISelectedOption) => {
    const variantSummaries = toVariantSummary(variants.value, globalConfig.enableAllVariants);
    const updatedSelectedOptions = handleUnavailableOptionSelection({
      options: optionsState.value,
      variants: variantSummaries,
      selectedOptions: selectedOptions.value,
      selectedOption,
    });

    selectedOptions.value = updatedSelectedOptions;

    optionsState.value = getOptionsWithUnavailabilityStatus(
      optionsState.value,
      variantSummaries,
      updatedSelectedOptions,
    );
  };

  watch(
    [variants, selectedOptions],
    () => {
      if (!variants.value) {
        return;
      }
      const selectedOptionsWithDefault =
        selectedOptions.value.length === 0
          ? setDefaultSelectedOptions(options.value)
          : selectedOptions.value;

      if (selectedOptions.value.length === 0 && selectedOptionsWithDefault.length > 0) {
        selectedOptions.value = selectedOptionsWithDefault;
      }
      optionsState.value = getOptionsWithUnavailabilityStatus(
        options.value,
        toVariantSummary(variants.value, globalConfig.enableAllVariants),
        selectedOptionsWithDefault,
      );
    },
    { immediate: true, deep: true },
  );

  return {
    options: optionsState,
    selectedOptions,
    updateSelectedOptions,
  };
};

export default useProductVariants;
